import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Download, Files, Upload, MessageSquare, PenLine, ExternalLink, Loader2, Video } from "lucide-react";
import { fmtDate } from "./referralConstants";
import { DocActionNudge } from "@/components/DocActionNudge";
import { DocDigestSettings } from "@/components/DocDigestSettings";

const STATUS_TONE = {
  requested: "text-[#B7791F] bg-[#B7791F]/12", uploaded: "text-[#2563EB] bg-[#2563EB]/12",
  waiting_for_review: "text-[#2563EB] bg-[#2563EB]/12", under_review: "text-[#2C6480] bg-[#2C6480]/12",
  needs_clarification: "text-[#9C1E22] bg-[#9C1E22]/12", approved: "text-[#2F7D5A] bg-[#2F7D5A]/12",
  shared: "text-[#1C4358] bg-[#1C4358]/12", ready_for_signing: "text-[#7C3AED] bg-[#7C3AED]/12",
  signing_pending: "text-[#7C3AED] bg-[#7C3AED]/12", signed: "text-[#2F7D5A] bg-[#2F7D5A]/12",
  archived: "text-[#6B7280] bg-[#6B7280]/12",
};

const sectionOf = (d) => {
  if (d.status === "signed" || d.signing?.signing_status === "signed_externally" || d.signing?.signing_status === "completed") return "completed";
  if (d.signing) return "signing";
  if (d.status === "requested") return "requested";
  if (d.status === "needs_clarification") return "attention";
  if (d.uploaded_by === "You") return "mine";
  return "shared";
};
const SECTIONS = [
  { key: "attention", title: "Needs clarification" },
  { key: "requested", title: "Requested documents" },
  { key: "signing", title: "Review / signing documents" },
  { key: "shared", title: "Shared with you" },
  { key: "mine", title: "Uploaded by you" },
  { key: "completed", title: "Completed" },
];

export default function ExternalDocuments() {
  const nav = useNavigate();
  const [docs, setDocs] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [respondDoc, setRespondDoc] = useState(null);
  const [respondText, setRespondText] = useState("");
  const fileRef = useRef(null);
  const rowDocId = useRef(null);

  const load = () => api.get("/external/documents").then(({ data }) => setDocs(data.documents || [])).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);

  const dl = async (d) => { try { await downloadFile(`/external/documents/${d.id}/download`, d.filename || d.title); } catch (e) { toast.error(errMsg(e)); } };
  const upload = (id) => { rowDocId.current = id; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; const id = rowDocId.current;
    if (!file || !id) return;
    setBusyId(id);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/external/documents/${id}/upload`, fd); toast.success("Uploaded."); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusyId(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const sendRespond = async () => {
    if (!respondText.trim()) return;
    setBusyId(respondDoc.id);
    try { await api.post(`/external/documents/${respondDoc.id}/respond`, { message: respondText.trim() }); toast.success("Message sent."); setRespondDoc(null); setRespondText(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  if (!docs) return <div className="space-y-3"><Skeleton className="h-10" /><Skeleton className="h-20" /><Skeleton className="h-20" /></div>;

  const grouped = {};
  docs.forEach((d) => { const s = sectionOf(d); (grouped[s] = grouped[s] || []).push(d); });
  const scrollToAction = () => { const el = document.querySelector('[data-testid^="external-doc-section-"]'); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div data-testid="external-documents-page" className="space-y-5">
      <div className="flex items-center gap-2">
        <Files size={22} className="text-[#1C4358]" />
        <h1 className="font-display text-2xl font-bold text-[#252221]">Documents</h1>
        <span className="text-[13px] text-muted-foreground">{docs.length} total</span>
        <div className="ml-auto"><DocDigestSettings testid="external-doc-digest-settings" /></div>
      </div>
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="external-doc-file-input" />

      <DocActionNudge endpoint="/external/documents/action-summary" testid="external-doc-action-nudge" onOpen={scrollToAction} />

      {docs.length === 0 ? (
        <Card className="p-10 border-[#E6EDF2] text-center" data-testid="external-documents-empty">
          <span className="mx-auto h-12 w-12 rounded-2xl bg-[#1C4358]/10 text-[#1C4358] flex items-center justify-center"><Files size={22} /></span>
          <p className="text-[15px] text-muted-foreground mt-3">No documents yet. Documents shared with you and quotes you upload from a referral will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {SECTIONS.filter((s) => (grouped[s.key] || []).length).map((sec) => (
            <div key={sec.key} data-testid={`external-doc-section-${sec.key}`}>
              <h3 className="font-display font-semibold text-[15px] text-[#1C4358] mb-2">{sec.title}</h3>
              <div className="space-y-2">
                {grouped[sec.key].map((d) => {
                  const canUpload = ["requested", "needs_clarification"].includes(d.status);
                  return (
                    <div key={d.id} className="rounded-xl bg-white border border-[#E6EDF2] p-3" data-testid={`external-alldoc-${d.id}`}>
                      <div className="flex items-center gap-3">
                        <span className="h-9 w-9 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><FileText size={16} /></span>
                        <div className="min-w-0 flex-1">
                          <button onClick={() => d.referral_id && nav(`/external/referrals/${d.referral_id}`)} className="text-[14px] font-medium text-[#252221] hover:text-[#1C4358] truncate block text-left">{d.title}</button>
                          <div className="text-[11px] text-[#9AA5B1]">{d.doc_type_label} · {d.uploaded_by} · {fmtDate(d.updated_at)}</div>
                        </div>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[d.status] || STATUS_TONE.uploaded}`}>{d.status_label}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {canUpload && <Button size="sm" onClick={() => upload(d.id)} disabled={busyId === d.id} className="h-8 bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid={`external-alldoc-upload-${d.id}`}>{busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload</Button>}
                          {d.status === "needs_clarification" && <Button size="sm" variant="outline" onClick={() => setRespondDoc(d)} className="h-8 border-[#D6E0E7] text-[#1C4358] gap-1" data-testid={`external-alldoc-reply-${d.id}`}><MessageSquare size={13} /> Reply</Button>}
                          {d.has_file && <button onClick={() => dl(d)} className="text-[#1C4358] hover:text-[#9C1E22] px-1" data-testid={`external-alldoc-download-${d.id}`}><Download size={16} /></button>}
                        </div>
                      </div>
                      {d.response && <div className="mt-2 rounded-lg bg-[#F4F8FB] border border-[#E6EDF2] px-3 py-2 text-[12.5px] text-[#3A4A52]"><span className="font-medium text-[#1C4358]">MovingMyMama: </span>{d.response}</div>}
                      {d.signing && (
                        <div className="mt-2 rounded-lg bg-[#F7F3FF] border border-[#E9DEFB] px-3 py-2 text-[12.5px]" data-testid={`external-alldoc-signing-${d.id}`}>
                          <span className="text-[#7C3AED] font-medium inline-flex items-center gap-1"><PenLine size={12} /> Signing: {d.signing.signing_status_label}</span>
                          {d.signing.external_signing_link && <a href={d.signing.external_signing_link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#7C3AED] hover:underline" data-testid={`external-alldoc-signing-link-${d.id}`}><ExternalLink size={11} /> Open link</a>}
                          {d.signing.meeting_id && <span className="ml-2 inline-flex items-center gap-1 text-[#1C4358]"><Video size={11} /> Meeting scheduled</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!respondDoc} onOpenChange={(o) => { if (!o) { setRespondDoc(null); setRespondText(""); } }}>
        <DialogContent data-testid="external-doc-respond-dialog">
          <DialogHeader><DialogTitle className="text-[#1C4358]">Reply to MovingMyMama</DialogTitle>
            <DialogDescription className="text-[13px]">{respondDoc?.response ? `“${respondDoc.response}”` : "Send a note about this document."}</DialogDescription>
          </DialogHeader>
          <Textarea value={respondText} onChange={(e) => setRespondText(e.target.value)} rows={3} placeholder="Type your reply…" data-testid="external-doc-respond-text" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRespondDoc(null); setRespondText(""); }}>Cancel</Button>
            <Button onClick={sendRespond} disabled={busyId === respondDoc?.id} className="bg-[#1C4358] hover:bg-[#153341] text-white" data-testid="external-doc-respond-send">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
